import { ChangeDetectorRef, Directive } from '@angular/core';
import { Router } from '@angular/router';
import { DictionaryService } from '@app/core/services/bff/dictionary.service';
import { UserProfileBffService, UserProfileInfo } from '@app/core/services/bff/user-profile.service';
import { LoadingBarService } from '@core/components/spinners/loading-bar/loading-bar.service';
import {
  AuthService,
  ContextService,
  FeaturesService,
  LanguageService,
  MetaService,
  ProfileService,
} from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { Language, Subject, User, UserServiceCategory, UserStatusEnum } from 'lingo2-models';
import { isEqual } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { switchMap, takeUntil, tap } from 'rxjs/operators';

@Directive()
export class BaseUserProfileSubpageComponent extends ChangableComponent {
  public loading = false;
  public accountRoute: string;
  public isMe: boolean;
  public isGuest: boolean;

  protected _me: User;
  protected _user: User;
  protected _profile: UserProfileInfo;

  public subjects: Subject[] = [];
  public languages: Language[] = [];
  public usCategories: UserServiceCategory[] = [];

  public _language: Language;

  constructor(
    protected router: Router,
    protected authService: AuthService,
    protected contextService: ContextService,
    protected profileService: ProfileService,
    protected dictionaryService: DictionaryService,
    protected languageService: LanguageService,
    protected userProfileBff: UserProfileBffService,
    protected features: FeaturesService,
    protected meta: MetaService,
    protected loadingBar: LoadingBarService,
    protected cdr: ChangeDetectorRef,
  ) {
    super(cdr);
    this.loadReferences$.pipe(takeUntil(this.destroyed$)).subscribe();
    this.loadUserData$.pipe(takeUntil(this.destroyed$)).subscribe();
  }

  public get user(): User {
    return this._user;
  }

  public get me(): User {
    return this._me;
  }

  public get isUserServices(): boolean {
    return !!this.user?.stats?.min_price_tier && !!this.user?.stats?.max_price_tier;
  }

  protected get getMe$(): Observable<User> {
    return this.contextService.me$.pipe(
      tap((me) => {
        if (!this._me || !isEqual(this._me, me)) {
          return (this._me = me);
        }
      }),
    );
  }

  protected get loadReferences$(): Observable<any> {
    return combineLatest([
      this.dictionaryService.subjec2ts,
      this.dictionaryService.languages,
      this.dictionaryService.uscategories,
      this.languageService.language$,
    ]).pipe(
      tap(([subjects, languages, usCategories, language]) => {
        if (!this.subjects || !isEqual(this.subjects, subjects)) {
          this.subjects = subjects;
        }
        if (!this.languages || !isEqual(this.languages, languages)) {
          this.languages = languages;
        }
        if (!this.usCategories || !isEqual(this.usCategories, usCategories)) {
          this.usCategories = usCategories;
        }
        if (!this._language || !isEqual(this._language, language)) {
          this._language = language;
        }
      }),
    );
  }

  protected get loadUserData$(): Observable<any> {
    return combineLatest([this.getUser$, this.getMe$]);
  }

  protected get getUser$(): Observable<UserProfileInfo> {
    return this.contextService.user$.pipe(
      tap((user) => {
        if (!this._user || !isEqual(this._user, user)) {
          return (this._user = user);
        } else {
          return this._user;
        }
      }),
      switchMap(() => this.getUserProfile$),
    );
  }

  protected get getUserProfile$(): Observable<UserProfileInfo> {
    return this.contextService.bffProfile$.pipe(
      tap((profile) => {
        if (!this._profile || !isEqual(this._user, profile)) {
          return (this._profile = profile);
        } else {
          return this._profile;
        }
      }),
    );
  }

  public goEditProfile(route: string) {
    if (this.me && this.me.status !== UserStatusEnum.guest) {
      this.navigateRoute(route);
    } else {
      this.authService.showAuthModal(() => this.navigateRoute(route));
    }
  }

  private navigateRoute(route: string) {
    this.router.navigateByUrl(route).then(() => true);
  }
}
